import type { PatientPortalSavedItemBalance } from "@/types/patient-portal";
import type { PublicServiceDetail } from "@/types/service";

import type { SelectedServiceInstance } from "@/stores/self-schedule-store";

export interface PrepaidCoverageAllocation {
  serviceId: string;
  selectedCount: number;
  coveredCount: number;
}

export interface GetPrepaidCoverageByServiceInput {
  selectedServiceInstances: Pick<SelectedServiceInstance, "id">[];
  selectedServices: Pick<PublicServiceDetail, "id">[] | undefined;
  savedItems: Pick<
    PatientPortalSavedItemBalance,
    "itemId" | "quantityRemaining"
  >[];
}

export function getPrepaidCoverageByService({
  selectedServiceInstances,
  selectedServices,
  savedItems,
}: GetPrepaidCoverageByServiceInput): Record<
  string,
  PrepaidCoverageAllocation
> {
  const selectedCounts = new Map<string, number>();
  const orderedServiceIds: string[] = [];
  const selections =
    selectedServiceInstances.length > 0
      ? selectedServiceInstances
      : (selectedServices ?? []);

  for (const selection of selections) {
    if (!selection.id) continue;
    if (!selectedCounts.has(selection.id)) {
      orderedServiceIds.push(selection.id);
    }
    selectedCounts.set(
      selection.id,
      (selectedCounts.get(selection.id) ?? 0) + 1,
    );
  }

  const availablePrepaidCountByService = new Map<string, number>();
  for (const savedItem of savedItems) {
    if (!savedItem.itemId) continue;
    const remaining = Math.floor(savedItem.quantityRemaining);
    if (!Number.isFinite(remaining) || remaining <= 0) continue;
    availablePrepaidCountByService.set(
      savedItem.itemId,
      (availablePrepaidCountByService.get(savedItem.itemId) ?? 0) + remaining,
    );
  }

  return orderedServiceIds.reduce<Record<string, PrepaidCoverageAllocation>>(
    (coverage, serviceId) => {
      const selectedCount = selectedCounts.get(serviceId) ?? 0;
      coverage[serviceId] = {
        serviceId,
        selectedCount,
        coveredCount: Math.min(
          selectedCount,
          availablePrepaidCountByService.get(serviceId) ?? 0,
        ),
      };
      return coverage;
    },
    {},
  );
}

export function getPrepaidCoverageCopy(
  allocation: Pick<PrepaidCoverageAllocation, "selectedCount" | "coveredCount">,
): string | null {
  const { selectedCount, coveredCount } = allocation;
  if (coveredCount <= 0) return null;
  if (coveredCount === 1 && selectedCount === 1) {
    return "Using your prepaid credit";
  }
  if (coveredCount === selectedCount) {
    return `Using ${coveredCount} prepaid credits`;
  }
  return `Using ${coveredCount} of ${selectedCount} prepaid credits`;
}
